"use client";

import React, { useState } from "react";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2 } from "lucide-react";
import { registerInstitution } from "@/actions/registration";
import { AccountConnectedCard } from "../account-connected-card";
import { cn } from "@/lib/utils";

interface AuthenticatedUser {
  id: string;
  email: string;
  name: string | null;
}

interface InstitutionRegistrationFormProps {
  authenticatedUser?: AuthenticatedUser | null;
}

const institutionSchema = z.object({
  email: z.string().email("Invalid email address"),
  fullName: z.string().min(2, "Name must be at least 2 characters"),
  phone: z.string().optional(),
  institutionName: z.string().min(2, "Institution name is required"),
  institutionContact: z.string().optional(),
  institutionContactPhone: z.string().optional(),
  exhibitionInterest: z.boolean().default(false),
});

type InstitutionFormData = z.infer<typeof institutionSchema>;

export function InstitutionRegistrationForm({
  authenticatedUser,
}: InstitutionRegistrationFormProps) {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const form = useForm<InstitutionFormData>({
    resolver: zodResolver(institutionSchema),
    defaultValues: {
      email: authenticatedUser?.email || "",
      fullName: authenticatedUser?.name || "",
      institutionName: "",
      exhibitionInterest: false,
    },
  });

  const onSubmit = async (data: InstitutionFormData) => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const submitData = {
        email: data.email || "",
        fullName: data.fullName || "",
        phone: data.phone,
        institutionName: data.institutionName || "",
        institutionContact: data.institutionContact,
        institutionContactPhone: data.institutionContactPhone,
        exhibitionInterest: data.exhibitionInterest,
      };

      const result = await registerInstitution(submitData, authenticatedUser?.id);

      if (result.success) {
        if (authenticatedUser) {
          router.push("/success?type=institution&verified=true");
        } else {
          router.push(
            `/verification-pending?email=${encodeURIComponent(submitData.email)}&type=institution`,
          );
        }
      } else {
        setErrorMessage(
          result.message ||
            "Registration failed. Please check your details and try again.",
        );
      }
    } catch (error) {
      console.error("Registration error:", error);
      setErrorMessage(
        "An unexpected error occurred. Please try again later.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <AccountConnectedCard user={authenticatedUser} />
      {errorMessage && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{errorMessage}</p>
        </div>
      )}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Email Field */}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  Email Address
                  {authenticatedUser && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                      <CheckCircle2 className="h-3 w-3" />
                      Verified
                    </span>
                  )}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="email"
                    placeholder="contact@institution.edu"
                    disabled={!!authenticatedUser}
                    className={cn(
                      authenticatedUser && "bg-gray-50 cursor-not-allowed",
                    )}
                  />
                </FormControl>
                {authenticatedUser && (
                  <p className="text-xs text-green-600 mt-1">
                    Via Edukate Africa account
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Full Name Field */}
          <FormField
            control={form.control}
            name="fullName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Full Name</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Your name"
                    disabled={isLoading}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Institution Name Field */}
          <FormField
            control={form.control}
            name="institutionName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Institution Name</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="e.g., University of Technology"
                    disabled={isLoading}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Institution Contact Field */}
          <FormField
            control={form.control}
            name="institutionContact"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Institution Contact Person (Optional)</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Primary contact name"
                    disabled={isLoading}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Phone Field */}
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone Number (Optional)</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    disabled={isLoading}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Institution Contact Phone Field */}
          <FormField
            control={form.control}
            name="institutionContactPhone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Institution Contact Phone (Optional)</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    disabled={isLoading}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Exhibition Interest Field */}
          <FormField
            control={form.control}
            name="exhibitionInterest"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                <FormControl>
                  <input
                    type="checkbox"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    disabled={isLoading}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                </FormControl>
                <FormLabel className="font-normal cursor-pointer">
                  I'm interested in an exhibition booth
                </FormLabel>
              </FormItem>
            )}
          />

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-600 hover:bg-blue-700"
            size="lg"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Registering...
              </>
            ) : (
              "Complete Registration"
            )}
          </Button>

          <p className="text-center text-xs text-gray-600">
            {authenticatedUser
              ? "Your registration will be instantly verified"
              : "We'll send you a verification email to confirm your registration"}
          </p>
        </form>
      </Form>
    </div>
  );
}
